import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateRange, DateRangePicker, SingleInputDateRangeField  } from "@mui/x-date-pickers-pro";
import { Box, InputAdornment, IconButton } from "@mui/material";
import { isValid } from "date-fns";

//icon
import Calendar from "@mui/icons-material/Event";

//locales
import ruLocale from "date-fns/locale/ru";
import ukLocale from "date-fns/locale/uk";
import enLocale from "date-fns/locale/en-US";
import esLocale from "date-fns/locale/es";
import trLocale from "date-fns/locale/tr";
import koLocale from "date-fns/locale/ko";
import elLocale from "date-fns/locale/el";
import frLocale from "date-fns/locale/fr";
import ptLocale from "date-fns/locale/pt";
import thLocale from "date-fns/locale/th";
import viLocale from "date-fns/locale/vi";
import idLocale from "date-fns/locale/id";
import zhLocale from "date-fns/locale/zh-CN";

//styles
import { useStyles } from "./styles";

const localeMap = {
  en: enLocale,
  ru: ruLocale,
  uk: ukLocale,
  es: esLocale,
  tr: trLocale,
  ko: koLocale,
  el: elLocale,
  fr: frLocale,
  pt: ptLocale,
  th: thLocale,
  vi: viLocale,
  id: idLocale,
  zh: zhLocale,
};

interface ICDateRangePickerProps {
  startDate: any,
  endDate: any,
  onChange: (start: Date, end: Date) => void;
  label?: string;
  validateDates?: boolean;
  onValidationError?: (error: string, dates: { start: any, end: any }) => void;
}


const CDateRangePicker: React.FC<ICDateRangePickerProps> = ({
  startDate,
  endDate,
  onChange,
  label = 'common.forms.fields.custom_dates',
  validateDates = false,
  onValidationError,
}): JSX.Element => {
  const { t, i18n } = useTranslation();
  const styles = useStyles();

  const [locale, setLocale] = useState<object>(localeMap.en);

  const [value, setValue] = React.useState<DateRange<Date>>(() => {
    const safeStartDate = startDate && isValid(new Date(startDate)) ? new Date(startDate) : null;
    const safeEndDate = endDate && isValid(new Date(endDate)) ? new Date(endDate) : null;
    return [safeStartDate, safeEndDate];
  });

  const validateDateRange = (start: any, end: any): { isValid: boolean; error?: string } => {
    if (!validateDates) {
      return { isValid: true };
    }

    if (!start) {
      return { isValid: false, error: 'Start date is required' };
    }

    const startDate = new Date(start);
    if (!isValid(startDate)) {
      return { isValid: false, error: 'Invalid start date provided' };
    }

    // Check if start date year is less than 1970
    if (startDate.getFullYear() < 1970) {
      return { isValid: false, error: 'Start date year cannot be less than 1970' };
    }

    const endDateToCheck = end;
    const endDate = new Date(endDateToCheck);
    if (!isValid(endDate)) {
      return { isValid: false, error: 'Invalid end date provided' };
    }

    // Check if end date year is less than 1970
    if (endDate.getFullYear() < 1970) {
      return { isValid: false, error: 'End date year cannot be less than 1970' };
    }

    // Check if end date is before start date
    if (endDate.getTime() < startDate.getTime()) {
      return { isValid: false, error: 'End date cannot be before start date' };
    }

    return { isValid: true };
  };

  const handleDateChange = (newValue: DateRange<Date>) => {
    const [start, end] = newValue;

    setValue(newValue);

    // Check if both dates are present and valid
    if (start && end && isValid(start) && isValid(end)) {
      // Always check for year < 1970, regardless of validateDates setting
      if (start.getFullYear() < 1970 || end.getFullYear() < 1970) {
        console.warn('Date year is less than 1970, onChange will not be called');
        if (onValidationError) {
          const errorMsg = start.getFullYear() < 1970
            ? 'Start date year cannot be less than 1970'
            : 'End date year cannot be less than 1970';
          onValidationError(errorMsg, { start, end });
        }
        return;
      }

      // Check if end date is before start date using getTime() for reliable comparison
      if (end.getTime() < start.getTime()) {
        console.warn('End date is before start date, onChange will not be called');
        if (onValidationError) {
          onValidationError('End date cannot be before start date', { start, end });
        }
        return;
      }

      const validation = validateDateRange(start, end);

      if (!validation.isValid) {
        console.error('Date validation failed:', validation.error);
        if (onValidationError) {
          onValidationError(validation.error!, { start, end });
        }
        return;
      }

      // Call onChange when both dates are valid
      onChange(start, end);
    }
  };

  useEffect(() => {
    const safeStartDate = startDate && isValid(new Date(startDate)) ? new Date(startDate) : null;
    const safeEndDate = endDate && isValid(new Date(endDate)) ? new Date(endDate) : null;

    setValue(() => [
      safeStartDate,
      safeEndDate,
    ]);
  }, [startDate, endDate]);

  useEffect(() => {
    if (i18n.language === 'ru') {
      setLocale(localeMap.ru);
    }
    if (i18n.language === 'en') {
      setLocale(localeMap.en);
    }
    if (i18n.language === 'uk') {
      setLocale(localeMap.uk);
    }
    if (i18n.language === 'es') {
      setLocale(localeMap.es);
    }
    if (i18n.language === 'tr') {
      setLocale(localeMap.tr);
    }
    if (i18n.language === 'ko') {
      setLocale(localeMap.ko);
    }
    if (i18n.language === 'el') {
      setLocale(localeMap.el);
    }
    if (i18n.language === 'fr') {
      setLocale(localeMap.fr);
    }
    if (i18n.language === 'pt') {
      setLocale(localeMap.pt);
    }
		if (i18n.language === 'th') {
      setLocale(localeMap.th);
    }
		if (i18n.language === 'vi') {
      setLocale(localeMap.vi);
    }
		if (i18n.language === 'id') {
      setLocale(localeMap.id);
    }
    if (i18n.language === 'zh') {
      setLocale(localeMap.zh);
    }
  }, [i18n.language]);

  return (
    <Box className={styles.field}>
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
        <DateRangePicker
          label={t(label)}
          //autoFocus
          value={value}
          onChange={handleDateChange}
          slots={{
            field: SingleInputDateRangeField,
          }}
          slotProps={{
            textField: {
              size: 'small',
              InputProps: {
                endAdornment: <InputAdornment position="end">
                  <IconButton size="small" className={styles.iconButton} sx={{ padding: '2px' }}>
                    <Calendar color="action" />
                  </IconButton>
                </InputAdornment>,
              },
            },
            actionBar: ({ wrapperVariant }) => {
              return {
                actions: wrapperVariant === 'desktop' ? [] : ['accept'],
              }
            },
          }}
          minDate={new Date("2015-01-01T00:00")}
        />
      </LocalizationProvider>
    </Box>
  );
};

export default CDateRangePicker;
